import React from 'react';
import { motion } from 'framer-motion';

import {
  Badge,
  Button,
  LinkBox,
  LinkOverlay,
  Heading,
  Flex,
  Box,
  Menu,
  MenuButton,
  IconButton,
  MenuList,
  MenuItem,
  MenuGroup,
  Icon,
  icons,
  TapTheme,
} from '@tapresearch/ui/components';
import { Styles as S } from './SiteNav';

const MotionItem = motion(MenuItem);
const MotionBox = motion(Box);

const iconMotion = {
  rest: {
    color: TapTheme.colors.gray[500],
    scale: 0.9,
    rotate: 0,
    opacity: 0.8,
    transition: {
      type: 'spring',
      bounce: 0.1,
    },
  },
  hover: {
    scale: [0.9, 1, 1.1, 1],
    color: 'var(--chakra-colors-blue-500)',
    rotate: [0, 0, 30, -10, 0],
    opacity: 1,
    transition: {
      type: 'spring',
      duration: 0.5,
      bounce: 0.5,
    },
  },
};

export const ProductMenu = ({ currentProduct, isMobile, asset, user }) => {
  const isSupplier = !!user.organization.supplier;
  const isCustomer = !!user.organization.customer;
  const noOps =
    user.organization_membership.role ===
    asset.constants.ORGANIZATION_ROLE_CUSTOMER_SUCCESS;

  const {
    TAP_PRODUCT_SNAPSHOT,
    TAP_PRODUCT_MONETIZATION,
    TAP_PRODUCT_SAMPLE,
    TAP_PRODUCT_PLAYER_INSIGHTS,
  } = asset.constants;
  const { TapResearchProducts } = asset.active_enum;

  const productKey =
    isSupplier && currentProduct === TAP_PRODUCT_SNAPSHOT
      ? TAP_PRODUCT_PLAYER_INSIGHTS
      : currentProduct;

  const productIcons = {
    [TAP_PRODUCT_SNAPSHOT]: icons.fa.RegChartBar,
    [TAP_PRODUCT_SAMPLE]: icons.io4.MdGlobe,
    [TAP_PRODUCT_MONETIZATION]: icons.md.OutlineAttachMoney,
    [TAP_PRODUCT_PLAYER_INSIGHTS]: icons.md.PersonSearch,
  };

  return (
    <>
      <Menu autoSelect={false}>
        {!isMobile && TapResearchProducts[productKey] ? (
          <MenuButton
            as={Button}
            aria-label="Product Menu"
            className="product-btn"
            rightIcon={<Icon boxSize={6} ml={0} icon={icons.io5.Apps} />}
            role="group"
            {...S.NavButton}
            {...Styles.CurrentProductButton}
          >
            <Flex {...Styles.CurrentProduct}>
              <Flex align="center">
                <Icon
                  {...Styles.MenuButtonIcon}
                  boxSize={6}
                  icon={productIcons[productKey]}
                />
              </Flex>
              <Heading {...Styles.MenuButtonHeading}>
                {TapResearchProducts[productKey].name}
              </Heading>
            </Flex>
          </MenuButton>
        ) : (
          <MenuButton
            as={IconButton}
            aria-label="Product Menu"
            icon={<Icon boxSize={6} icon={icons.io5.Apps} />}
            {...S.NavButton}
          />
        )}

        <MenuList {...Styles.MenuDropdown}>
          {isSupplier ? (
            <>
              <Monetization
                constants={asset.constants}
                currentProduct={productKey}
                TapResearchProducts={TapResearchProducts}
                productIcons={productIcons}
              />
              {isCustomer && user.organization.developer_surveys_active && (
                <Research
                  isSupplier
                  constants={asset.constants}
                  currentProduct={productKey}
                  TapResearchProducts={TapResearchProducts}
                  productIcons={productIcons}
                  noOps={noOps}
                />
              )}
            </>
          ) : (
            <Research
              isSupplier={false}
              constants={asset.constants}
              currentProduct={productKey}
              TapResearchProducts={TapResearchProducts}
              productIcons={productIcons}
              noOps={noOps}
            />
          )}
        </MenuList>
      </Menu>
    </>
  );
};

const Monetization = ({
  constants,
  TapResearchProducts,
  productIcons,
  currentProduct,
}) => {
  const { TAP_PRODUCT_MONETIZATION } = constants;
  return (
    <MenuGroup title="App Monetization">
      <MotionItem
        initial="rest"
        whileHover="hover"
        animate="rest"
        as={LinkBox}
        {...Styles.MenuItem}
      >
        <MotionBox variants={iconMotion}>
          <Icon
            {...Styles.MenuItemIcon}
            icon={productIcons[TAP_PRODUCT_MONETIZATION]}
          />
        </MotionBox>

        <Box>
          <LinkOverlay
            href={TapResearchProducts[TAP_PRODUCT_MONETIZATION].link}
          >
            <Heading {...Styles.MenuItemHeading}>
              {TapResearchProducts[TAP_PRODUCT_MONETIZATION].name}
              {TAP_PRODUCT_MONETIZATION === currentProduct && (
                <Badge {...Styles.CurrentBadge}>Current</Badge>
              )}
            </Heading>
            <MotionBox {...Styles.MenuItemText}>
              <span>
                {TapResearchProducts[TAP_PRODUCT_MONETIZATION].description}
              </span>
            </MotionBox>
          </LinkOverlay>
        </Box>
      </MotionItem>
    </MenuGroup>
  );
};

const Research = ({
  isSupplier,
  constants,
  TapResearchProducts,
  productIcons,
  currentProduct,
  noOps = false,
}) => {
  const {
    TAP_PRODUCT_PLAYER_INSIGHTS,
    TAP_PRODUCT_SNAPSHOT,
    TAP_PRODUCT_SAMPLE,
  } = constants;
  return isSupplier ? (
    <MenuGroup className="research" title="Research">
      <MotionItem
        initial="rest"
        whileHover="hover"
        animate="rest"
        as={LinkBox}
        {...Styles.MenuItem}
      >
        <MotionBox variants={iconMotion}>
          <Icon
            {...Styles.MenuItemIcon}
            icon={productIcons[TAP_PRODUCT_PLAYER_INSIGHTS]}
          />
        </MotionBox>
        <Box>
          <LinkOverlay
            href={TapResearchProducts[TAP_PRODUCT_PLAYER_INSIGHTS].link}
          >
            <Heading {...Styles.MenuItemHeading}>
              {TapResearchProducts[TAP_PRODUCT_PLAYER_INSIGHTS].name}
              {TAP_PRODUCT_PLAYER_INSIGHTS === currentProduct && (
                <Badge {...Styles.CurrentBadge}>Current</Badge>
              )}
            </Heading>
            <MotionBox {...Styles.MenuItemText}>
              <span>
                {TapResearchProducts[TAP_PRODUCT_PLAYER_INSIGHTS].description}
              </span>
            </MotionBox>
          </LinkOverlay>
        </Box>
      </MotionItem>
    </MenuGroup>
  ) : (
    <MenuGroup className="research" title="Research">
      <MotionItem
        initial="rest"
        whileHover="hover"
        animate="rest"
        as={LinkBox}
        {...Styles.MenuItem}
      >
        <MotionBox variants={iconMotion}>
          <Icon
            {...Styles.MenuItemIcon}
            icon={productIcons[TAP_PRODUCT_SNAPSHOT]}
          />
        </MotionBox>
        <Box>
          <LinkOverlay href={TapResearchProducts[TAP_PRODUCT_SNAPSHOT].link}>
            <Heading {...Styles.MenuItemHeading}>
              {TapResearchProducts[TAP_PRODUCT_SNAPSHOT].name}
              {TAP_PRODUCT_SNAPSHOT === currentProduct && (
                <Badge {...Styles.CurrentBadge}>Current</Badge>
              )}
            </Heading>
            <MotionBox {...Styles.MenuItemText}>
              <span>
                {TapResearchProducts[TAP_PRODUCT_SNAPSHOT].description}
              </span>
            </MotionBox>
          </LinkOverlay>
        </Box>
      </MotionItem>
      {!noOps && (
        <MotionItem
          initial="rest"
          whileHover="hover"
          animate="rest"
          as={LinkBox}
          {...Styles.MenuItem}
        >
          <MotionBox variants={iconMotion}>
            <Icon
              {...Styles.MenuItemIcon}
              icon={productIcons[TAP_PRODUCT_SAMPLE]}
            />
          </MotionBox>

          <Box>
            <LinkOverlay href={TapResearchProducts[TAP_PRODUCT_SAMPLE].link}>
              <Heading {...Styles.MenuItemHeading}>
                {TapResearchProducts[TAP_PRODUCT_SAMPLE].name}
              </Heading>
              <MotionBox {...Styles.MenuItemText}>
                <span>
                  {TapResearchProducts[TAP_PRODUCT_SAMPLE].description}
                </span>
              </MotionBox>
            </LinkOverlay>
          </Box>
        </MotionItem>
      )}
    </MenuGroup>
  );
};

const Styles = {
  CurrentProduct: {
    h: '100%',
    rounded: 0,
    mr: 0,
    align: 'center',
    p: '0 15px',
  },
  CurrentBadge: {
    colorScheme: 'blue',
    variant: 'subtle',
    fontWeight: 'bold',
    p: '4px',
  },
  CurrentProductButton: {
    h: 10,
    px: 2,
    p: 0,
    overflow: 'hidden',
    rounded: 'md',
    border: '1px solid',
    borderColor: 'gray.200',
    alignItems: 'stretch',

    sx: {
      '.chakra-button__icon': {
        ml: 0,
        color: 'gray.700',
        h: '100%',
        display: 'flex',
        alignItems: 'center',
        p: '0 10px',
        borderLeft: '1px solid #EEE',
        transitionProperty: 'common',
        transitionDuration: 'normal',
      },
      _active: {
        bg: 'gray.50',
        borderColor: 'blue.500',
        '.chakra-button__icon': {
          bg: 'gray.50',
          color: 'blue.500',
        },
      },
      _hover: {
        bg: 'gray.50',
        borderColor: 'blue.500',
        '.chakra-button__icon': {
          bg: 'gray.50',
          color: 'blue.500',
        },
      },
    },
  },
  ProductButton: {
    h: 12,
    mr: 6,
    ml: 4,
    variant: 'ghost',
    colorScheme: 'blue',
    bg: 'none',
    boxShadow: 'sm',
    border: '1px solid',
    borderColor: '#ccc',
    borderRadius: '6px',
    transitionDuration: '.3s',
    _hover: {
      bg: TapTheme.fn.rgba(TapTheme.colors.blue[500], 0.12),
      borderColor: TapTheme.fn.rgba(TapTheme.colors.blue[500], 0.12),
    },
    _active: {
      color: 'blue.500',
      bg: TapTheme.fn.rgba(TapTheme.colors.blue[500], 0.05),
    },
  },
  MenuButtonHeading: {
    mr: 3,
    fontSize: 'md',
    color: '#555',
    justifyContent: 'center',
    fontWeight: 'semibold',
    letterSpacing: 'wider',
    textTransform: 'uppercase',
    lineHeight: 'normal',
  },
  MenuButtonIcon: {
    color: 'blue.500',
    mr: 3,
  },
  MenuItem: {
    py: 3,
    px: 4,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    w: 'calc(100% - 15px)',
    m: '0 auto',
    rounded: 'md',
    border: '2px solid',
    borderColor: 'white',
    transitionProperty: 'border, background',
    transitionDuration: 'normal',
    overflow: 'hidden',
    maxW: { base: 'fit-content', md: 'none' },
    sx: {
      a: {
        textDecoration: 'none',
      },
    },
    _hover: {
      borderColor: 'blue.500',
    },
    _focus: {
      bg: 'blue.50',
    },
  },
  MenuDropdown: {
    zIndex: 'popover',
    boxShadow: 'lg',
    display: 'flex',
    flexDirection: 'column',
    minW: '300px',
    maxW: { base: '100%', md: '450px' },
    w: { base: '100vw', md: 'auto' },
    py: 5,
    px: 3,
    alignItems: 'flex-start',
    sx: {
      '.chakra-menu__group': {
        w: 'full',
        display: 'flex',
        flexDirection: 'column',
      },

      '.chakra-menu__group__title': {
        color: 'gray.400',
        px: 2,
        pt: 2,
        pb: 1,
        mt: 0,
        mx: 3,
        fontWeight: 'normal',
      },
      '.chakra-menu__group:not(:first-of-type)': {
        mt: 2,
      },
    },
  },
  MenuItemHeading: {
    fontSize: '16px',
    color: 'gray.700',
    py: '3px',
    justifyContent: 'space-between',
    align: 'center',
    fontWeight: 'semibold',
  },
  MenuItemText: {
    fontSize: '.85rem',
    fontWeight: 'medium',
    color: 'gray.600',
    pt: 1,
  },
  MenuItemIcon: {
    boxSize: 6,
    color: 'inherit',
    mr: 4,
  },
};
